import MoneyRendererV3 from '@components/Money/MoneyRendererV3/MoneyRendererV3';
import Typography from '@components/v2/ui/Typography/Typography';
import * as R from 'ramda';
import { memo } from 'react';
import PriceCompositionMarker from '../../../../../components/Money/PriceCompositionMarker';
import { ProductGridTestIds } from '../../../../../utils/testIds/productGrid.testIds';
import ProductPricesTooltip from '../../../../products/prices/components/ProductPricesTooltip';
import useOrderProductPriceV2 from '../../hooks/useOrderProductPriceV2';
import { calculateTotalOrderProduct } from '../../orderProducts.utils';
import { useCombinationPartsQuantity } from '../grid/store/useCombinationPartsQuantity';
import { useStoreBackedGrid } from '../grid/store/useStoreBackedGrid';
import { OrderProductPriceV2Props } from '../types/orderProductPriceV2.types';

const OrderProductPriceV2 = ({
  grid,
  isFinishedOrder,
  partsQuantity,
  combinationKey,
  showTotal = true,
  hidePricesTooltip = false,
  total,
  originalPriceTestId,
  discountedPriceTestId,
}: OrderProductPriceV2Props) => {
  const displayPartsQuantity = useCombinationPartsQuantity({
    combinationKey,
    fallback: partsQuantity,
  });
  const storeBackedGrid = useStoreBackedGrid({ combinationKey, grid });
  const displayTotal = combinationKey ? calculateTotalOrderProduct(storeBackedGrid) : total;
  const {
    closeProductPriceTooltip,
    isProductPriceTooltipOpen,
    openProductPriceTooltip,
    pricesConfig,
    showInfoTooltip,
    tooltipPrices,
    hasManyPrices,
  } = useOrderProductPriceV2({
    grid: storeBackedGrid,
    isFinishedOrder,
  });

  const canShowPricesTooltip = showInfoTooltip && !hidePricesTooltip;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-end',
      }}
    >
      {!R.isNil(pricesConfig.minWholesalePrice) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            columnGap: '2px',
          }}
        >
          <Typography
            noWrap
            variant="body1"
            color="text.primary"
            testId={ProductGridTestIds.COMBINATION_PIECES_COUNTER}
          >
            {displayPartsQuantity} x
          </Typography>
          <ProductPricesTooltip
            open={canShowPricesTooltip}
            prices={tooltipPrices}
            hasPriceVariation={hasManyPrices}
            closeModal={closeProductPriceTooltip}
            isModalOpen={isProductPriceTooltipOpen}
          >
            <PriceCompositionMarker
              isActive={canShowPricesTooltip}
              onClick={openProductPriceTooltip}
            >
              <MoneyRendererV3
                color="textPrimary"
                horizontalAlign="right"
                promotionValue={pricesConfig.minPromotionalPrice}
                size="medium"
                value={pricesConfig.minWholesalePrice}
                originalPriceTestId={originalPriceTestId}
                discountedPriceTestId={discountedPriceTestId}
              />
            </PriceCompositionMarker>
          </ProductPricesTooltip>
        </div>
      )}
      {showTotal && !R.isNil(displayTotal) && (
        <MoneyRendererV3
          value={displayTotal}
          color="textSecondary"
          valueTestId={ProductGridTestIds.COMBINATION_LINE_TOTAL}
        />
      )}
    </div>
  );
};

export default memo(OrderProductPriceV2);
